import {
	CommonParameterUnits,
	ComponentValueValidationInstanceMetadataSchema,
	ComponentValueValidationResultMetadataSchema,
	formatNumericalSIValue,
	parseNumericalSIValue,
} from '@cadstrom/sigma-engine-shared';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { StatusBadge } from '../components/status-badge.js';

import type { RequirementInstanceResult } from '@cadstrom/sigma-engine-shared';

type ComponentValidationDetailPanelProps = {
	row: RequirementInstanceResult;
};

export function ComponentValidationDetailPanel({ row }: ComponentValidationDetailPanelProps) {
	const parseResult = ComponentValueValidationResultMetadataSchema.safeParse(row.resultMetadata ?? {});
	const instanceMetadataParse = ComponentValueValidationInstanceMetadataSchema.safeParse(row.instanceMetadata ?? {});

	if (!parseResult.success || !instanceMetadataParse.success) {
		return <div>Error parsing metadata</div>;
	}

	const resultMetadata = parseResult.data;
	const instanceMetadata = instanceMetadataParse.data;

	const formattedPackageValue = formatPackageValue(instanceMetadata.packageData.value ?? '');
	return (
		<div className='p4 flex flex-col gap-4'>
			<Paper className='p-4'>
				<Typography className='mb-2 text-lg font-semibold text-gray-a11 uppercase'>Validation Result</Typography>

				<div className='bg-card border-bottom flex items-center gap-2 rounded-md p-3'>
					<StatusBadge status={row.status} size='small' />
					{resultMetadata.message}
				</div>

				<Table className='mt-2' size='small'>
					<TableBody>
						<TableRow hover>
							<TableCell className='w-1/2'>Project Value</TableCell>
							<TableCell>{resultMetadata.projectValue}</TableCell>
						</TableRow>
						<TableRow hover>
							<TableCell className='w-1/2'>Package Value</TableCell>
							<TableCell>{resultMetadata.packageValue}</TableCell>
						</TableRow>
						<TableRow hover>
							<TableCell className='w-1/2'>Project Raw Value</TableCell>
							<TableCell>{resultMetadata.projectRawValue}</TableCell>
						</TableRow>
						<TableRow hover>
							<TableCell className='w-1/2'>Package Raw Value</TableCell>
							<TableCell>{resultMetadata.packageRawValue}</TableCell>
						</TableRow>
					</TableBody>
				</Table>

				<div className='mt-4 flex flex-col'>
					<Typography className='mb-2 text-lg font-semibold text-gray-a11 uppercase'>Design Details</Typography>
					<Typography className='mb-2 font-semibold'>Metadata</Typography>
					<Table size='small'>
						<TableHead className='bg-blue-a3'>
							<TableRow>
								<TableCell className='w-1/2'>Property</TableCell>
								<TableCell>Value</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{instanceMetadata.designData.metadata &&
								Object.entries(instanceMetadata.designData.metadata).map(([key, value]) => {
									let displayValue: string;
									if (typeof value === 'object' && value !== null) {
										displayValue = JSON.stringify(value);
									} else if (value === undefined) {
										displayValue = '';
									} else {
										displayValue = String(value);
									}
									return (
										<TableRow key={key} hover>
											<TableCell className='w-1/2'>{key}</TableCell>
											<TableCell>{displayValue}</TableCell>
										</TableRow>
									);
								})}
						</TableBody>
					</Table>
					<Typography className='mt-4 mb-2 font-semibold'>Draw Properties</Typography>
					<Table size='small'>
						<TableHead className='bg-blue-a3'>
							<TableRow>
								<TableCell className='w-1/2'>Property</TableCell>
								<TableCell>Value</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{instanceMetadata.designData.drawProperties &&
								Object.entries(instanceMetadata.designData.drawProperties).map(([key, value]) => {
									return (
										<TableRow key={key} hover>
											<TableCell className='w-1/2'>{key}</TableCell>
											<TableCell>{value.value}</TableCell>
										</TableRow>
									);
								})}
						</TableBody>
					</Table>
				</div>

				<div className='mt-4 flex flex-col'>
					<Typography className='mb-2 text-lg font-semibold text-gray-a11 uppercase'>Package Data</Typography>
					<Table className='mt-2' size='small'>
						<TableHead className='bg-blue-a3'>
							<TableRow>
								<TableCell className='w-1/2'>Property</TableCell>
								<TableCell>Value</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow hover>
								<TableCell className='w-1/2'>Package Value</TableCell>
								<TableCell>{formattedPackageValue}</TableCell>
							</TableRow>
							<TableRow hover>
								<TableCell className='w-1/2'>Manufacturer</TableCell>
								<TableCell>{instanceMetadata.packageData.manufacturer ?? ''}</TableCell>
							</TableRow>
							<TableRow hover>
								<TableCell className='w-1/2'>Part Number</TableCell>
								<TableCell>{instanceMetadata.packageData.partNumber ?? ''}</TableCell>
							</TableRow>
							<TableRow hover>
								<TableCell className='w-1/2'>Resolution Type</TableCell>
								<TableCell>{instanceMetadata.packageData.resolutionType ?? ''}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</div>
			</Paper>
		</div>
	);
}

function formatPackageValue(value: string): string {
	if (!value) {
		return '';
	}

	const numericValue = parseNumericalSIValue(value, CommonParameterUnits.unitless);
	return formatNumericalSIValue(numericValue);
}
